import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { emptyArray } from '@/consts';
import { api } from '@/utils/api';

/**
 * @type {import('react').Context<{
 *   user: any,
 *   userData: any,
 *   groups: any[],
 *   loading: boolean,
 *   submissions: any[],
 *   events: any[],
 *   isVerified: boolean,
 *   socialAccounts: any[],
 *   setSocialAccounts: import('react').Dispatch<import('react').SetStateAction<any[]>>,
 * }>}
 */
const AuthContext = createContext({
  user: null,
  userData: null,
  groups: [],
  loading: true,
  submissions: [],
  events: [],
  isVerified: false,
  socialAccounts: [],
  setSocialAccounts: () => {
    /* empty */
  },
});
AuthContext.displayName = 'AuthContext';

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({
  children,
  initialUser = null,
  initialGroups,
  initialSubmissions,
  initialEvents,
  initialUserData = null,
  initialCsrfToken,
  initialSocialAccounts = emptyArray,
}) {
  const [user, setUser] = useState(initialUser);
  const [groups, setGroups] = useState(initialGroups);
  const [submissions, setSubmissions] = useState(initialSubmissions);
  const [events, setEvents] = useState(initialEvents);
  const [userData, setUserData] = useState(initialUserData);
  const [csrfToken, setCsrfToken] = useState(initialCsrfToken);
  const [isVerified, setIsVerified] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState(initialSocialAccounts);
  const [loading, setLoading] = useState(initialUser === null);

  useEffect(() => {
    if (initialUser !== null) {
      setLoading(false);
      return;
    }
    setUser(initialUser);
    setGroups(initialGroups);
    setSubmissions(initialSubmissions);
    setEvents(initialEvents);
    setUserData(initialUserData);
    setCsrfToken(initialCsrfToken);
    setSocialAccounts(initialSocialAccounts);
    setIsVerified(false);
    setLoading(true);
  }, [initialUser]);

  useEffect(() => {
    api.defaults.headers['X-CSRFToken'] = csrfToken;
  }, [csrfToken]);

  const value = useMemo(
    () => ({
      user,
      userData,
      groups,
      loading,
      submissions,
      events,
      isVerified,
      socialAccounts,
      setSocialAccounts,
    }),
    [user, userData, groups, loading, submissions, events, isVerified, socialAccounts],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialCsrfToken: PropTypes.string.isRequired,
  initialEvents: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialSocialAccounts: PropTypes.arrayOf(PropTypes.object),
  initialSubmissions: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialUser: PropTypes.object,
  initialUserData: PropTypes.object,
};
